import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { explain } from '@/shared/api/problem'
import { ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import { Panel } from '@/components/erp/primitives'
import {
  ClearanceBadge, CollectedAt, InitialsAvatar, LabeledBar, MomentumBadge, Pill, ScoreRing,
  SeverityBadge, SideBadge, SubHeader, fmtDate, fmtKES, label,
} from '@/features/dossier/components/primitives'
import { can as hasPermission, useSession } from '@/shared/auth/session'
import {
  getTarget, listPlays, listSurfaces, stageHistory,
  type Play, type StageTransition, type Surface, type Target,
} from './api'
import { KillChainPanel } from './KillChainPanel'
import { PlaybookPanel } from './PlaybookPanel'
import { SurfaceEditor } from './SurfaceEditor'

/**
 * One target: its scores, its surfaces, its play-book and how it got to its stage.
 *
 * ## The stage history is the defensible half
 *
 * `gateSatisfiedJson` records what the service checked and the ids it checked. Six months
 * later, when a claim is challenged in public, "we moved to DEPLOY on the 14th" is worthless
 * and "on the strength of these two items from these two independent sources, with this play
 * cleared by this reviewer" is a defence. So it is rendered, not hidden behind a debug flag.
 *
 * ## Evidence strength is read-only everywhere
 *
 * There is no control for it on this page because there is none on the server. It is derived
 * from the linked items; an analyst who could type it would type 90.
 */
export function TargetPage() {
  const { id = '' } = useParams()
  const [target, setTarget] = useState<Target | null>(null)
  const [surfaces, setSurfaces] = useState<Surface[]>([])
  const [plays, setPlays] = useState<Play[]>([])
  const [history, setHistory] = useState<StageTransition[]>([])
  const [error, setError] = useState<string | null>(null)
  const authorities = useSession((st) => st.authorities)

  const load = useCallback(async () => {
    try {
      setError(null)
      const [t, s, p, h] = await Promise.all([
        getTarget(id), listSurfaces(id), listPlays(id), stageHistory(id),
      ])
      setTarget(t)
      setSurfaces(s)
      setPlays(p)
      setHistory(h)
    } catch (err) {
      setError(explain(err))
    }
  }, [id])

  useEffect(() => { void load() }, [load])

  if (error && !target) return <ErrorStrip message={error} />
  if (!target) return <LoadingBlock label="Opening the target" />

  return (
    <div className="space-y-3">
      <Panel>
        <div className="flex flex-wrap items-start gap-3 p-3">
          <InitialsAvatar name={target.displayName} size={40} />
          <div className="min-w-48 flex-1 space-y-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <h2 className="text-[15px] font-semibold">{target.displayName}</h2>
              <SideBadge side={target.sideCode} />
              <ClearanceBadge clearance={target.clearanceCode} />
              <Pill className="border-border">{label(target.killChainStageCode)}</Pill>
            </div>
            <div className="flex flex-wrap gap-3 text-[11px] text-muted-foreground">
              <span>{label(target.targetTypeCode)}</span>
              {target.seatType && <span>{target.seatType}</span>}
              <span>Priority {target.priority}</span>
              <span>War chest {fmtKES(target.warChestKes)}</span>
              <MomentumBadge momentum={target.momentumCode} />
              <CollectedAt at={target.scoresAsOf} prefix="scored" />
            </div>
            <Link to={`/dossier/${target.subjectId}`}
                  className="text-[11px] text-primary underline">
              Open the subject's file →
            </Link>
          </div>
          <div className="flex gap-3">
            <ScoreRing value={target.threatScore} label="Threat" tone="danger" />
            <ScoreRing value={target.vulnerabilityScore} label="Vulnerability" tone="warn" />
            <ScoreRing value={target.electabilityScore} label="Electability" />
          </div>
        </div>
      </Panel>

      {/* ONE list of surfaces, not two. This block used to render every surface read-only
          and then again underneath with the controls, which is what made the section
          unreadable. SurfaceEditor owns the whole thing now: a scannable list on the left,
          one exposure in full on the right. */}
      <Panel>
        <div className="p-2">
          {hasPermission(authorities, 'opposition:assess') ? (
            <SurfaceEditor targetId={target.id} subjectId={target.subjectId}
                           surfaces={surfaces} onChanged={() => void load()} />
          ) : (
            <>
              <SubHeader>Attack surfaces</SubHeader>
              {surfaces.length === 0 ? (
                <p className="py-2 text-[12px] text-muted-foreground">
                  No exposures recorded.
                </p>
              ) : (
                <div className="space-y-2">
                  {surfaces.map((s) => (
                    <div key={s.id} className="rounded border border-border p-2">
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <span className="text-[12px] font-medium">{s.surface}</span>
                        <div className="flex items-center gap-1.5">
                          <Pill className="border-border">{label(s.categoryCode)}</Pill>
                          {s.statusCode === 'STALE'
                            ? <SeverityBadge severity="HIGH" />
                            : <Pill className="border-border">{label(s.statusCode)}</Pill>}
                        </div>
                      </div>
                      <p className="mt-1 text-[11px] text-muted-foreground">{s.rationale}</p>
                      <div className="mt-2 grid gap-2 sm:grid-cols-4">
                        <LabeledBar label="Severity" value={s.severity} />
                        <LabeledBar label="Public exposure" value={s.publicExposure} />
                        <LabeledBar label="Defensibility" value={s.defensibility} />
                        <LabeledBar label="Evidence (derived)" value={s.evidenceStrength}
                                    tone="bg-emerald-500" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </Panel>

      <Panel>
        <PlaybookPanel
          plays={plays}
          surfaces={surfaces}
          targetId={target.id}
          canPlan={hasPermission(authorities, 'opposition:plan')}
          canReview={hasPermission(authorities, 'opposition:legal-review')}
          onChanged={() => void load()}
        />
      </Panel>

      <Panel>
        <KillChainPanel
          target={target}
          canOperate={hasPermission(authorities, 'opposition:operate')}
          canPlan={hasPermission(authorities, 'opposition:plan')}
          onMoved={() => void load()}
        />
      </Panel>

      <Panel>
        <div className="p-2">
          <SubHeader>How it got here</SubHeader>
          {history.length === 0 ? (
            <p className="text-[12px] text-muted-foreground">No moves yet.</p>
          ) : (
            <ul className="space-y-1">
              {history.map((h) => (
                <li key={h.id} className="rounded border border-border p-1.5">
                  <div className="flex items-baseline justify-between gap-2 text-[11px]">
                    <span>{label(h.fromStageCode)} → <strong>{label(h.toStageCode)}</strong></span>
                    <span className="text-muted-foreground">{fmtDate(h.movedAt)}</span>
                  </div>
                  {h.note && <p className="text-[11px] text-muted-foreground">{h.note}</p>}
                  {/* The gate evidence, rendered. This is what a challenged claim rests on. */}
                  <details className="mt-0.5">
                    <summary className="cursor-pointer text-[10px] text-muted-foreground">
                      What the gate checked
                    </summary>
                    <pre className="axis-table-scroll mt-1 rounded bg-muted p-1 text-[10px]">
                      {formatJson(h.gateSatisfiedJson)}
                    </pre>
                  </details>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Panel>
    </div>
  )
}

function formatJson(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2)
  } catch {
    return raw
  }
}
